import React, { useState, useEffect, useRef } from "react";
import { Plus, MoreHorizontal, Play, ChevronDown, Trash2, Check } from "lucide-react";
import { GOLD } from "../constants/theme";
import { bamConfirm } from "./dialog";
import { IS_TOUCH } from "../../utils/platform";

// Toolbar shown above the favorites grid: filter chips for lists, a
// "+ New list" affordance, and a sort dropdown.
export function FavListsToolbar({
  C, dark, lists, filter, setFilter, sort, setSort,
  onCreateList, onRenameList, onDeleteList, favCount, membership,
}) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // listId being edited
  const [editName, setEditName] = useState("");
  const [sortOpen, setSortOpen] = useState(false);
  const sortLabel = {
    recent: "Recently saved",
    oldest: "Oldest first",
    title: "Title A–Z",
    section: "Section",
  }[sort] || "Recently saved";

  // Count favs in each list for the chip labels.
  const counts = {};
  Object.values(membership || {}).forEach(arr => {
    (arr || []).forEach(id => { counts[id] = (counts[id] || 0) + 1; });
  });

  const submitNew = () => {
    const n = newName.trim();
    if (!n) { setAdding(false); return; }
    onCreateList(n);
    setNewName("");
    setAdding(false);
  };
  const submitRename = (id) => {
    const n = editName.trim();
    if (n) onRenameList(id, n);
    setEditing(null);
    setEditName("");
  };

  const chip = (active, children, onClick, extra = {}) => (
    <div onClick={onClick}
      style={{padding:"6px 14px",borderRadius:20,fontSize:12,fontWeight:700,cursor:"pointer",
        border:`1px solid ${active?GOLD:C.border}`,
        background:active?GOLD:"transparent",
        color:active?"#111":C.textMid,
        transition:"all .15s",
        display:"flex",alignItems:"center",gap:6,
        ...extra}}>
      {children}
    </div>
  );

  return (
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
      {chip(filter === "all", <>All <span style={{fontSize:10,opacity:.7}}>{favCount}</span></>, ()=>setFilter("all"))}
      {lists.map(l => {
        if (editing === l.id) {
          return (
            <div key={l.id} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:20,border:`1px solid ${GOLD}`,background:`${GOLD}12`}}>
              <input autoFocus={!IS_TOUCH} value={editName} onChange={e=>setEditName(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter")submitRename(l.id); if(e.key==="Escape"){setEditing(null);setEditName("");}}}
                onBlur={()=>submitRename(l.id)}
                style={{background:"transparent",border:"none",outline:"none",fontSize:12,fontWeight:700,color:C.text,fontFamily:"'DM Sans',sans-serif",width:100}}/>
            </div>
          );
        }
        return (
          <div key={l.id}
            onDoubleClick={()=>{setEditing(l.id);setEditName(l.name);}}
            title="Double-click to rename"
            style={{display:"flex",alignItems:"center",gap:0}}>
            {chip(filter === l.id,
              <>{l.name} <span style={{fontSize:10,opacity:.7}}>{counts[l.id]||0}</span></>,
              ()=>setFilter(filter === l.id ? "all" : l.id),
              { borderTopRightRadius:0, borderBottomRightRadius:0 }
            )}
            <div onClick={async ()=>{
                if(!(await bamConfirm({ title: `Delete collection "${l.name}"?`, message: "Bookmarks themselves aren't removed.", confirmLabel: "Delete", danger: true }))) return;
                onDeleteList(l.id);
              }}
              title="Delete collection"
              style={{padding:"6px 8px",borderRadius:"0 20px 20px 0",borderLeft:"none",
                border:`1px solid ${filter === l.id ? GOLD : C.border}`,
                background: filter === l.id ? GOLD : "transparent",
                color: filter === l.id ? "#111" : C.textDim,
                cursor:"pointer",display:"flex",alignItems:"center"}}>
              <Trash2 size={11}/>
            </div>
          </div>
        );
      })}
      {adding ? (
        <div style={{display:"flex",alignItems:"center",gap:4,padding:"4px 12px",borderRadius:20,border:`1px solid ${GOLD}`,background:`${GOLD}12`}}>
          <input autoFocus={!IS_TOUCH} value={newName} onChange={e=>setNewName(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter")submitNew(); if(e.key==="Escape"){setAdding(false);setNewName("");}}}
            onBlur={submitNew}
            placeholder="Collection name"
            style={{background:"transparent",border:"none",outline:"none",fontSize:12,fontWeight:700,color:C.text,fontFamily:"'DM Sans',sans-serif",width:140}}/>
        </div>
      ) : (
        chip(false, <><Plus size={12}/> New collection</>, ()=>setAdding(true), { borderStyle:"dashed" })
      )}

      {/* Sort dropdown, pushed right */}
      <div style={{marginLeft:"auto",position:"relative"}}>
        <div onClick={()=>setSortOpen(o=>!o)}
          style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:12,fontWeight:600,color:C.textMid,cursor:"pointer"}}>
          Sort: <span style={{color:C.text,fontWeight:700}}>{sortLabel}</span> <ChevronDown size={12}/>
        </div>
        {sortOpen && (
          <div className="bam-menu-in" style={{position:"absolute",top:"calc(100% + 4px)",right:0,zIndex:20,minWidth:180,
            background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:10,overflow:"hidden",
            boxShadow:dark?"0 12px 40px rgba(0,0,0,0.55)":"0 8px 24px rgba(0,0,0,0.12)"}}>
            {[["recent","Recently saved"],["oldest","Oldest first"],["title","Title A–Z"],["section","Section"]].map(([k,label])=>(
              <div key={k} onClick={()=>{setSort(k);setSortOpen(false);}}
                style={{display:"flex",alignItems:"center",gap:8,padding:"9px 12px",fontSize:12.5,color:C.text,cursor:"pointer",
                  background: sort === k ? `${GOLD}14` : "transparent"}}>
                {sort === k ? <Check size={12} color={GOLD}/> : <span style={{width:12}}/>}
                {label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Individual favorite card with a "•••" add-to-list menu. Clicking the
// card itself fires onPlay (navigate to the item's section and open it) —
// the corner buttons stopPropagation so remove/menu don't trigger playback.
/**
 * The "put this in a collection" menu.
 *
 * It lived inside FavCard, which only renders in the My Bookmarks block on
 * the dashboard. So a coach could group drills, but only afterwards, by
 * going back to the dashboard and working through what she had saved.
 *
 * A member wrote in: "I want to put them in groups based on specific players
 * I develop. It would be amazing if I could do that as I'm scanning through
 * the database." That last sentence is the whole feature request, and the
 * menu it needed already existed in the wrong place.
 *
 * So it is its own component now, and the drill card and the drill detail
 * panel both use it.
 *
 * `onPick` takes the whole job rather than just the toggle: on a card for a
 * drill that isn't saved yet, choosing a collection has to save it first.
 * Asking a coach to bookmark and then group is two gestures for one
 * decision, and the decision is "this one is for Maya".
 *
 * `suggestions` are roster names. She wants collections that are players,
 * and the players are already in My Players, so offering them stops her
 * typing the same fourteen names she has already typed once.
 */
export function CollectionMenu({
  C, dark, lists = [], memberLists = [], suggestions = [],
  onPick, onCreate, onClose, align = "left", top = 34,
}) {
  const [newName, setNewName] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.(); };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const submitNew = () => {
    const n = newName.trim();
    if (n) onCreate?.(n);
    setNewName(""); setAddingNew(false);
  };

  // Roster names that aren't collections yet. A player who already has one
  // is in the list above and would read as a duplicate down here.
  const unused = suggestions.filter(
    n => n && !lists.some(l => (l.name || "").toLowerCase() === n.toLowerCase())
  ).slice(0, 6);

  return (
    <div ref={ref} onClick={e => e.stopPropagation()}
      className={`bam-menu-in${align === "left" ? " from-tl" : ""}`}
      style={{ position: "absolute", top, [align]: 7, zIndex: 40, minWidth: 200, maxWidth: 260,
        background: dark ? "#141414" : "#FFFFFF", border: `1px solid ${C.cardBorder}`,
        borderRadius: 10, overflow: "hidden",
        boxShadow: dark ? "0 12px 40px rgba(0,0,0,0.55)" : "0 8px 24px rgba(0,0,0,0.12)" }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: GOLD, letterSpacing: 1.2,
        textTransform: "uppercase", padding: "9px 12px 6px" }}>Add to collection</div>

      {lists.length === 0 && !addingNew && unused.length === 0 && (
        <div style={{ fontSize: 11, color: C.textDim, padding: "0 12px 8px", lineHeight: 1.5 }}>
          No collections yet. Create one below, then anything you save can go in it.
        </div>
      )}

      <div style={{ maxHeight: 210, overflow: "auto" }}>
        {lists.map(l => {
          const on = memberLists.includes(l.id);
          return (
            <div key={l.id} onClick={() => onPick?.(l.id)}
              className="btn"
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                fontSize: 12.5, color: C.text, cursor: "pointer",
                background: on ? `${GOLD}14` : "transparent" }}>
              <div style={{ flexShrink: 0, width: 16, height: 16, borderRadius: 4,
                border: `1.5px solid ${on ? GOLD : C.border}`, background: on ? GOLD : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                {on && <Check size={10} color="#111" strokeWidth={3}/>}
              </div>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
                whiteSpace: "nowrap" }}>{l.name}</span>
            </div>
          );
        })}
      </div>

      {unused.length > 0 && (
        <>
          <div style={{ height: 1, background: C.border, margin: "4px 0" }}/>
          <div style={{ fontSize: 9, fontWeight: 800, color: C.textDim, letterSpacing: 1.1,
            textTransform: "uppercase", padding: "7px 12px 5px" }}>From your roster</div>
          {unused.map(n => (
            <div key={n} onClick={() => onCreate?.(n)} className="btn"
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px",
                fontSize: 12.5, color: C.textMid, cursor: "pointer" }}>
              <Plus size={11} color={GOLD} style={{ flexShrink: 0 }}/>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
                whiteSpace: "nowrap" }}>{n}</span>
            </div>
          ))}
        </>
      )}

      <div style={{ height: 1, background: C.border, margin: "4px 0" }}/>
      {addingNew ? (
        <div style={{ padding: "8px 10px" }}>
          <input autoFocus={!IS_TOUCH} value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") submitNew();
              if (e.key === "Escape") { setAddingNew(false); setNewName(""); } }}
            onBlur={submitNew} placeholder="Player or collection name"
            style={{ width: "100%", padding: "7px 10px", borderRadius: 7,
              border: `1px solid ${C.border}`, background: dark ? "#0E0E0E" : "#FAFAFA",
              color: C.text, fontSize: 12, outline: "none",
              fontFamily: "'DM Sans',sans-serif", boxSizing: "border-box" }}/>
        </div>
      ) : (
        <div onClick={() => setAddingNew(true)} className="btn"
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px",
            fontSize: 12.5, color: GOLD, fontWeight: 700, cursor: "pointer" }}>
          <Plus size={12}/> New collection
        </div>
      )}
    </div>
  );
}

export function FavCard({ item, tc, C, dark, memberLists, lists, onRemove, onToggleList, onCreateList, onPlay }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [thumbErr, setThumbErr] = useState(false);
  const thumb = item.muxPlaybackId
    // Both dimensions, or Mux ignores fit_mode and the portrait clips get a
    // dumb centre crop. The box is 96px tall; this is 2x for retina.
    ? `https://image.mux.com/${item.muxPlaybackId}/thumbnail.jpg?width=480&height=192&fit_mode=smartcrop`
    : item.thumb || null;
  const [newName, setNewName] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false); };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const submitNew = () => {
    const n = newName.trim();
    if (n) onCreateList(n);
    setNewName("");
    setAddingNew(false);
  };

  return (
    <div ref={ref} onClick={()=>onPlay && onPlay()} className={`bam-fav-card${onPlay?" btn":""}`}
      style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,overflow:"hidden",position:"relative",cursor:onPlay?"pointer":"default"}}>
      {/* A real frame from the drill when we have one. This was a flat block
          of colour picked off the section palette by id, which is fine as a
          fallback and reads as a bug when the video it stands for has a
          perfectly good thumbnail sitting on Mux. */}
      <div style={{height:96,background:tc,display:"flex",alignItems:"center",justifyContent:"center",
        position:"relative",overflow:"hidden"}}>
        {thumb && !thumbErr && (
          <img src={thumb} alt="" loading="lazy" onError={()=>setThumbErr(true)}
            className="bam-fav-thumb"
            style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}}/>
        )}
        {/* Reads over any frame, bright or dark. */}
        <div style={{position:"absolute",inset:0,background:thumb&&!thumbErr
          ? "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0.55) 100%)"
          : "linear-gradient(180deg, rgba(0,0,0,0.18), rgba(0,0,0,0.30))"}}/>
        <span style={{position:"relative",width:34,height:34,borderRadius:"50%",
          background:"rgba(0,0,0,0.5)",backdropFilter:"blur(4px)",
          border:"1px solid rgba(255,255,255,0.18)",
          display:"flex",alignItems:"center",justifyContent:"center"}} className="bam-fav-play">
          <Play size={14} color="#fff" fill="#fff" style={{marginLeft:2}}/>
        </span>
        {/* Remove from favorites */}
        <div onClick={(e)=>{e.stopPropagation(); onRemove();}}
          title="Remove from favorites"
          style={{position:"absolute",top:6,right:7,width:24,height:24,borderRadius:"50%",
            background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}
          className="btn">
          <svg width="11" height="11" viewBox="0 0 24 24" fill={GOLD} stroke={GOLD} strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </div>
        {/* Add-to-list trigger */}
        <div onClick={(e)=>{e.stopPropagation(); setMenuOpen(o=>!o);}}
          title="Add to list"
          style={{position:"absolute",top:6,left:7,width:24,height:24,borderRadius:"50%",
            background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}
          className="btn">
          <MoreHorizontal size={13} color="#fff"/>
        </div>
        {menuOpen && (
          <div onClick={e=>e.stopPropagation()} className="bam-menu-in from-tl"
            style={{position:"absolute",top:34,left:7,zIndex:30,minWidth:180,
              background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:10,overflow:"hidden",
              boxShadow:dark?"0 12px 40px rgba(0,0,0,0.55)":"0 8px 24px rgba(0,0,0,0.12)"}}>
            <div style={{fontSize:9,fontWeight:800,color:GOLD,letterSpacing:1.2,textTransform:"uppercase",padding:"9px 12px 6px"}}>Add to collection</div>
            {lists.length === 0 && !addingNew && (
              <div style={{fontSize:11,color:C.textDim,padding:"0 12px 6px",lineHeight:1.5}}>No collections yet - create one below.</div>
            )}
            {lists.map(l => {
              const on = memberLists.includes(l.id);
              return (
                <div key={l.id} onClick={()=>onToggleList(l.id)}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",fontSize:12.5,color:C.text,cursor:"pointer",
                    background: on ? `${GOLD}14` : "transparent"}}>
                  <div style={{flexShrink:0,width:16,height:16,borderRadius:4,border:`1.5px solid ${on?GOLD:C.border}`,background:on?GOLD:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {on && <Check size={10} color="#111" strokeWidth={3}/>}
                  </div>
                  <span style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.name}</span>
                </div>
              );
            })}
            <div style={{height:1,background:C.border,margin:"4px 0"}}/>
            {addingNew ? (
              <div style={{padding:"8px 10px"}}>
                <input autoFocus={!IS_TOUCH} value={newName} onChange={e=>setNewName(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter")submitNew(); if(e.key==="Escape"){setAddingNew(false);setNewName("");}}}
                  onBlur={submitNew}
                  placeholder="New collection name"
                  style={{width:"100%",padding:"7px 10px",borderRadius:7,border:`1px solid ${C.border}`,background:dark?"#0E0E0E":"#FAFAFA",color:C.text,fontSize:12,outline:"none",fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box"}}/>
              </div>
            ) : (
              <div onClick={()=>setAddingNew(true)}
                style={{display:"flex",alignItems:"center",gap:8,padding:"9px 12px",fontSize:12.5,color:GOLD,fontWeight:700,cursor:"pointer"}}>
                <Plus size={12}/> New collection…
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{padding:"10px 12px"}}>
        <div style={{fontSize:9,fontWeight:700,color:"#111",background:GOLD,padding:"2px 7px",borderRadius:8,display:"inline-block",marginBottom:5,letterSpacing:.5}}>{item.tag || item.category}</div>
        <div style={{fontSize:13,fontWeight:700,color:C.text,lineHeight:1.3}}>{item.title}</div>
        <div style={{fontSize:11,color:C.textDim,marginTop:2}}>{item.duration}</div>
        {memberLists.length > 0 && (
          <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:8}}>
            {memberLists.map(listId => {
              const l = lists.find(x => x.id === listId);
              if (!l) return null;
              return (
                <span key={listId} style={{fontSize:9,fontWeight:700,color:GOLD,background:`${GOLD}15`,border:`1px solid ${GOLD}44`,padding:"2px 6px",borderRadius:8,letterSpacing:.3}}>
                  {l.name}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
